

exports.__esModule = true;
exports.$Api = undefined;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.$getLatestCorrelationID = $getLatestCorrelationID;

var _jquery = require('jquery');

var _jquery2 = _interopRequireDefault(_jquery);

var _logger = require('xo-beaver/dist/logger');

var _event = require('./event');

var _class = require('./class');

var _util = require('./util');

var _promise = require('./promise');

var _config = require('./config');

var _error = require('./error');

var _loader = require('./loader');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/*
 API
 ---

 This layer is responsible for handling api requests. It has a number of responsibilities:

 - Caching responses
 - Transparently returning data that has been preloaded into the initial page response
 - Instrumentation
 - Batching multiple api calls into a single http request
 - Handling multiple types of responses and dispatching to the correct handlers
 - Emitting loading events for all http calls
 */

_config.$meta.headers = _config.$meta.headers || {};
_config.$meta.headers['x-cookies'] = _typeof(_config.$meta.headers['x-cookies']) !== 'object' ? JSON.parse(_config.$meta.headers['x-cookies'] || '{}') : _config.$meta.headers['x-cookies'];

// Are we in 'cookies disabled' mode? (Do we need to keep a local store of cookies)
function cookiesEnabled() {
    return _util.$util.cookiesEnabled() && window.location.hostname.indexOf('.paypal.com') > -1;
}

// Cache for api responses, when cache: true
var cache = {};

var latestCorrelationID = _config.$meta.correlationId;

// Window load promise
var windowLoad = _util.$util.memoize(function () {
    return _promise.$promise.resolver(function (resolve) {
        if (document.readyState === 'complete') {
            resolve();
        } else {
            window.addEventListener('load', resolve);
        }
    });
});

// Buffer for batch requests made within the same frame
var batchQueue = {};

// Backwards compat
window.pre = {};

// Transient cache resolvers
var transientCache = {};

// Transient cache resolvers
var transientCacheResolvers = {};

// Metabuilder
var metaBuilder = void 0;

// Add to transientCache
window.preload = function (method, url, data, name) {

    if (name) {
        window.pre[name] = {
            method: method,
            uri: url,
            res: data
        };
    }

    var key = method + ':' + url;
    var resolvers = transientCacheResolvers[key] || [];

    transientCache[key] = data;

    while (resolvers.length) {
        resolvers.pop().call();
    }
};

// Preload complete
var preloadComplete = false;

window.preloadComplete = function () {
    preloadComplete = true;
    Object.keys(transientCacheResolvers).forEach(function (key) {
        var resolvers = transientCacheResolvers[key] || [];

        while (resolvers.length) {
            resolvers.pop().call();
        }
    });
};

_logger.$logger.info(cookiesEnabled() ? 'cookies_enabled' : 'cookies_disabled');

var $Api = exports.$Api = _class.$Class.extend('$Api', {

    // Cache any GET responses indefinitely
    cache: false,

    // Http timeout for all requests
    timeout: 45000,

    // Default number of attempts on aborted requests and 500 server errors
    getAttempts: 3,
    postAttempts: 1,

    // POST on a resource with an action name myapi.action('foo') -> POST /my/api/foo
    action: function action(_action, options) {
        options.action = _action;
        return this.post(options);
    },

    // GET on a resource myapi.retrieve() -> GET /my/api
    retrieve: function retrieve(options) {
        options.method = 'get';
        return this.call(options);
    },

    // POST on a resource myapi.retrieve() -> POST /my/api
    post: function post(options) {
        options.method = 'post';
        return this.call(options);
    },

    // Generic handler for calling an api and dispatching requests to the right place
    call: function call(options) {
        var self = this;

        // Set up request options and params. `options` is an object which should contain everything
        // the request needs to run and handle the response

        options.api = this;
        options.uri = this.getURI(options.model, options.action);
        options.params = options.params || {};
        options.cache = options.cache || self.cache && options.method === 'get';
        options.name = this.buildAPIName(options);
        options.meta = this.buildMeta();
        options.transientError = options.transientError || false;
        options.cacheKey = _util.$util.buildURL(options.uri, options.params);

        _logger.$logger.info(options.name + '_call', {
            name: options.name,
            method: options.method,
            uri: options.uri
        });

        if (!options.silent) {
            _event.$event.emit('loading');
        }

        // Decide where to get the response
        return _promise.$promise.first([

        // If caching is enabled and we have the data in local cache, use the cache
        function () {
            if (options.cache && cache[options.cacheKey]) {
                return cache[options.cacheKey];
            }
        },

        // Otherwise if we have transient cache data for this api, we can use that
        function () {
            return _promise.$promise.providing(self.hasTransientCacheData(options), function () {

                // The reason we use self.attemptRequest here is so any errors in the cached response
                // can trigger the retry code path, as if it were a regular request
                return self.attemptRequest(options);
            });
        },

        // If we're in batch mode, queue up a batch request
        function () {
            if (options.batch) {
                return self.batchRequest(options);
            }
        },

        // Otherwise attempt a regular http request (at this point an http call is guaranteed)
        function () {
            return self.attemptRequest(options);
        }])['finally'](function () {

            if (!options.silent) {
                _event.$event.emit('loaded');
            }
        }).then(function (res) {

            // Cache the response if we need to
            if (options.cache) {
                cache[options.cacheKey] = res;
            }

            // Handle the response
            return self.handleResponse(res.data, options);
        }, function (err) {

            // $BatchShortCircuit will be thrown if:
            //
            // 1. batch api call B depends on api call A, but-
            // 2. api call A fails
            //
            // In which case we end up in a situation where had it been a regular chain of events,
            // api B would never have been called in the first place.
            //
            // One possibility here is to return $unresolved, which prevents the promise from ever
            // resolving, but this has the potential to cause a lot of difficulty debugging and strange
            // corner cases.
            //
            // Instead we opt to simply reject the promise, and not call any of the response handlers

            if (err instanceof _error.$BatchShortCircuit) {
                return _promise.$promise.reject(err);
            }

            // Otherwise, handle the error normally
            return self.handleErrorResponse(err, options);
        });
    },

    // Set up a batch request for processing in the next frame
    batchRequest: function batchRequest(options) {

        // Batch Requests are an optimization layer on top of regular api calls. The purpose of this
        // layer is to combine multiple atomic api calls into a single http request, without forcing
        // developers to create large orchestration layers on the client/server with cross-cutting concerns.
        //
        // This layer will queue up multiple requests made in the same frame whenever batch-mode is enabled,
        // attach them all to an http call to /api/batch/:name, then on the response it will dispatch each
        // individual api call to the correct handler.
        //
        // The way this is implemented is to place each response in the transient api cache, then to yield
        // control to the regular api request handler, which will handle instrumentation and retries

        // For quick dev-time feedback...
        _util.$util.assert(options.batch.name, 'Must define a "name" for batch request: ' + options.batch);
        _util.$util.assert(options.batch.id, 'Must define a "id" for batch request: ' + options.batch);

        var name = options.batch.name;
        var id = options.batch.id;

        // Add the batch request options to the batchQueue for processing on the next frame
        var batch = batchQueue[name] = batchQueue[name] || {};

        batch[id] = options;

        // Fire off a batch request. This will be debounced: calls in the same frame will be combined to one
        return this.buildBatchRequest(name).then(function (results) {

            // We get back a mapping of id->response, so we now extract the response for our api
            // call. If we have no response, we can assume the call was never made, and short circuit.
            return results[id].then(function (result) {
                return result || _promise.$promise.reject(new _error.$BatchShortCircuit());
            });
        });
    },

    // Process all of the accumulated batch api requests
    buildBatchRequest: _promise.$promise.debounce(function (name) {

        var self = this;
        var batch = batchQueue[name];
        var batchIds = _class.$Class.keys(batch);
        var batchData = {};
        var headers = {};

        delete batchQueue[name];

        // If we have just a single request, we can short-circuit and just hit that api

        if (batchIds.length === 1) {

            var batchId = batchIds[0];
            var opts = batch[batchId];

            var results = {};

            results[batchId] = opts.api.attemptRequest(opts);
            return results;
        }

        // Build the request to send to the server side. This is an mapping of id->request, each containing
        // the method, uri, data, and dependencies for each api.
        //
        // Specifying a dependency for an api essentially means the back-end will only
        // call api-B if api-A returns ack=success. Otherwise there will be no response for api B.

        _util.$util.forEach(batch, function (options, id) {

            batchData[id] = {
                method: options.method,
                uri: options.api.getURI(options.model, options.action, true),
                data: options.data,
                params: options.params,
                dependencies: options.batch.dependencies
            };

            _util.$util.extend(headers, options.headers);
        });

        // Unfortunate circular dependency... $Api depends on another api.
        //
        // $batchApi is subject to all of the same rules regarding retries, so if for some reason the server
        // does not respond the first time, our batch request can still get through.

        return $batchApi.action(name, {
            data: batchData,
            headers: headers
        }).then(function (res) {

            // The final response will be a mapping of id->response-promise.
            //
            // However, we do not treat this data as our final response. Instead, we simply use it to
            // prime our transient cache, then proceed to jump to our regular request handler. This will
            // take care of retries and instrumentation as if this were a regular request.
            //
            // Note that a retry for $batchApi is different to a retry for an individual api. If
            // an *individual* api call fails, we do not want to re-do the entire batch call; instead we
            // can just retry the individual api call.

            return self.handleBatchResponse(batch, res.data);
        });
    }),

    // Process the responses for all of the batch requests we sent to the server
    handleBatchResponse: function handleBatchResponse(batch, data) {

        var self = this;

        var promises = {};

        // Loop over each individual api response contained in the batch response, and process the result

        _util.$util.forEach(batch, function (options, id) {

            // Store each response promise so we can check and wait for api dependencies

            promises[id] = _promise.$promise.run(function () {

                // Check if all of the dependencies are present for our individual api call. To do this
                // we must wait until the dependent api call is entirely complete, including any retries.

                var depsPresent = _promise.$promise.every(options.batch.dependencies || [], function (depName) {

                    if (!batch[depName]) {
                        _logger.$logger.info('missing_batch_dependency', {
                            dependency: depName,
                            available: Object.keys(batch).join('|')
                        });
                    }

                    return !batch[depName] || promises[depName].then(function (dependency) {
                        return dependency && dependency.data && dependency.data.ack === 'success';
                    });
                });

                // If our dependencies are present, we can continue, otherwise we are not interested in
                // the result of the individual api call - if its dependency is not present, it will not be.

                return _promise.$promise.providing(depsPresent, function () {

                    // If there is data returned for the individual call, add it to the transient cache

                    return _promise.$promise.providing(data[id], function (result) {

                        self.addTransientCacheData(options.method, options.api.getURI(options.model, options.action), result);
                    }).then(function () {

                        // Regardless of whether or not there is data, we can now attempt the request.
                        // This will either get the data from the transient cache, or make an http call,
                        // and handle any retries that need to be made.

                        return options.api.attemptRequest(options);
                    });
                });
            });
        });

        return promises;
    },

    // Handle the request and any retries, using either the transient cache or http calls
    attemptRequest: function attemptRequest(options) {
        var self = this;

        var attempts = options.method === 'get' ? this.getAttempts : this.postAttempts;

        // We allow a certain number of attempts/retries for each api call
        return _promise.$promise.attempt(attempts, function (remaining) {

            // Query the transient cache
            return self.getTransientCacheResponse(options).then(function (res) {

                // If we can get a response from the transient cache, then use it
                if (res) {
                    return res;
                }

                // Otherwise resort to making an http call
                return self.getHttpResponse(options);
            })['catch'](function (res) {

                // If our request was aborted without even a status code, force a single retry
                if ((!res || !res.status) && attempts === 1) {

                    _logger.$logger.warn('api_retry_aborted', {
                        method: options.method,
                        uri: options.uri
                    });

                    return self.getHttpResponse(options);
                }

                return _promise.$promise.reject(res);
            })['catch'](function (res) {

                // If our request was 401 denied (probably due to a CSRF error), force a single retry
                if (res.status === 401) {

                    _logger.$logger.warn('api_retry_401', {
                        method: options.method,
                        uri: options.uri
                    });

                    return self.getHttpResponse(options);
                }

                return _promise.$promise.reject(res);
            })['catch'](function (res) {

                // Set the correct response and ack for 401
                if (res.status === 401) {
                    return {
                        data: {
                            ack: 'permission_denied'
                        }
                    };
                }

                // If we have any remaining retries, log and reject
                if (remaining) {

                    _logger.$logger.warn('api_retry', {
                        method: options.method,
                        uri: options.uri
                    });

                    return _promise.$promise.reject(res);
                }

                // Otherwise reject the request with the appropriate error

                // We got ack=error in the response
                if (res && res.data && res.data.ack === 'error') {
                    return _promise.$promise.reject(new _error.$ApiError('api_error', { // eslint-disable-line no-reserved-keys
                        name: options.name,
                        method: options.method,
                        uri: options.uri,
                        stack: res.data.stack,
                        transient: options.transientError
                    }));
                }

                // We got a response error status
                else if (res && res.status) {
                        return _promise.$promise.reject(new _error.$ApiError('response_status_' + res.status, { // eslint-disable-line no-reserved-keys
                            uri: options.uri,
                            transient: options.transientError
                        }));
                    } else if (res && res.error) {
                        return _promise.$promise.reject(new _error.$ApiError('request_' + res.error, { // eslint-disable-line no-reserved-keys
                            uri: options.uri,
                            transient: options.transientError
                        }));
                    }

                    // The request was aborted
                    else {
                            return _promise.$promise.reject(new _error.$ApiError('request_aborted', { // eslint-disable-line no-reserved-keys
                                uri: options.uri,
                                transient: options.transientError
                            }));
                        }
            });
        });
    },

    // Get an api response via http
    getHttpResponse: function getHttpResponse(options) {
        var self = this;

        var startTime = _util.$util.now();

        var request = this.http(options);

        return request['finally'](function () {

            // Calculate the end-to-end duration of the request from the client's perspective
            options.duration = _util.$util.now() - startTime;
        })['catch'](function (res) {

            // Turn responses with an ack into a 'success' so it makes it to the standard handlers
            if (res && res.status && res.data && res.data.ack && res.data.ack !== 'error') {
                return res;
            }

            return _promise.$promise.reject(res);
        }).then(function (res) {
            self.saveResponseState(res);

            var loggerPayload = {
                name: options.name,
                method: options.method,
                uri: options.uri,
                server: res.data.server,
                time: options.duration,
                duration: options.duration,
                corrid: res && res.headers('Paypal-Debug-Id')
            };

            // In certain browsers we can match up this api call to the performance data from the browser
            if (window.performance && window.performance.getEntries) {
                _util.$util.forEach(window.performance.getEntries(), function (resource) {
                    if (resource.name && resource.name.indexOf(options.uri) > -1) {
                        _util.$util.extend(loggerPayload, resource);
                    }
                });
            }

            _logger.$logger.info('api_response', loggerPayload);

            if (res && res.status) {
                _logger.$logger.info('http_response_' + res.status);
            }

            return res;
        }, function (res) {
            self.saveResponseState(res);

            if (res && res.status) {
                _logger.$logger.info('http_response_' + res.status);
            }

            return _promise.$promise.reject(res);
        });
    },

    setTransientCache: function setTransientCache(data) {
        throw new Error('NotImplemented');
    },

    // Get an individual api response from the transient cache, as an object
    getTransientCacheData: function getTransientCacheData(options, pop) {

        if (!_config.$config.enablePreload) {
            return _promise.$promise.resolve();
        }

        var preMethod = options.method.toLowerCase();
        var preUri = _util.$util.buildURL(options.uri, options.params);

        var key = preMethod + ':' + preUri;

        return _promise.$promise.resolver(function (resolve) {

            function res() {
                var data = transientCache[key];

                if (pop) {
                    delete transientCache[key];
                }
                resolve(data);
            }

            transientCacheResolvers[key] = transientCacheResolvers[key] || [];
            transientCacheResolvers[key].push(res);

            if (transientCache[key] || document.readyState === 'complete' || preloadComplete) {
                return res();
            }

            windowLoad().then(res);
        });
    },

    // Check if the transient cache has the data for a particular api
    hasTransientCacheData: function hasTransientCacheData(options) {
        return this.getTransientCacheData(options, false).then(function (data) {
            return Boolean(data);
        });
    },

    // Add data to the transient cache
    addTransientCacheData: function addTransientCacheData(method, uri, data) {
        window.preload(method, uri, data);
    },

    // Get an individual api response from transient cache, as a res promise with the correct status code
    getTransientCacheResponse: function getTransientCacheResponse(options) {

        return this.getTransientCacheData(options, true).then(function (data) {

            // Preload data does not come with a response code, so massage it into a response promise
            if (data) {
                if (data.ack === 'error') {
                    return _promise.$promise.reject({
                        status: 500,
                        data: data
                    });
                } else if (data.ack === 'permission_denied') {
                    return _promise.$promise.reject({
                        status: 401,
                        data: data
                    });
                } else if (data.ack === 'contingency' || data.ack === 'validation_error') {
                    return {
                        status: 400,
                        data: data
                    };
                } else {
                    return {
                        status: 200,
                        data: data
                    };
                }
            }

            // Log a cache miss if we are currently in the first-page load
            else if (options.method === 'get' && !_loader.$loader.firstLoad() && !options.silent) {
                    _logger.$logger.info('preload_cache_miss', {
                        uri: options.uri
                    });
                }
        });
    },

    getHeaders: function getHeaders(options) {

        if (!_config.$meta.headers['x-csrf-jwt'] && !_config.$meta.csrfJwt) {
            _logger.$logger.warn('missing_csrf_jwt');
        }

        var headers = {
            'X-Requested-With': 'XMLHttpRequest',
            'x-csrf-jwt': _config.$meta.headers['x-csrf-jwt'] || _config.$meta.csrfJwt
        };

        if (!cookiesEnabled()) {
            headers['x-cookies'] = JSON.stringify(_config.$meta.headers['x-cookies']);
        }

        if (options.headers) {
            _util.$util.extend(headers, options.headers);
        }

        return headers;
    },
    http: function http(options) {
        return this.httpJQuery(options);
    },


    /*
     // Raw http call
    httpAngular: function(options) {
         let request = {
            method: options.method,
            url: options.uri,
            data: {
                data: options.data,
                meta: options.meta
            },
            headers: this.getHeaders(options),
            params: options.params,
            requestType: 'json',
            responseType: 'json',
            timeout: this.timeout
        };
         if (options.method === 'get' && options.meta && Object.keys(options.meta).length) {
            request.params.meta = JSON.stringify(options.meta);
        }
         return $http(request);
    },
     */

    httpJQuery: function httpJQuery(options) {

        var request = {
            method: options.method,
            data: options.method === 'get' ? options.params : JSON.stringify({
                data: options.data,
                meta: options.meta || {}
            }),
            headers: this.getHeaders(options),
            timeout: this.timeout
        };

        if (options.method === 'post') {
            request.headers['Content-Type'] = 'application/json;charset=UTF-8';
        } else if (options.method === 'get') {
            request.data.meta = JSON.stringify(options.meta);
        }

        return _promise.$promise.resolver(function (resolve, reject) {

            function getRes(res, data) {
                return {
                    status: res.status,
                    data: data,
                    headers: function headers(name) {
                        return res.getResponseHeader(name);
                    }
                };
            }

            request.success = function (data, status, res) {
                return resolve(getRes(res, data));
            };

            request.error = function (res) {
                if (res && res.status) {
                    if (res.status >= 400) {
                        return reject(getRes(res, res.responseJSON));
                    } else {
                        return resolve(getRes(res, res.responseJSON));
                    }
                }

                return reject({
                    status: 0,
                    headers: _util.$util.noop,
                    error: res && res.statusText
                });
            };

            _jquery2['default'].ajax(options.uri, request);
        });
    },

    // Save cookies and jwt-csrf token from the response
    saveResponseState: function saveResponseState(res) {

        if (res.headers('x-csrf-jwt')) {
            _config.$meta.headers['x-csrf-jwt'] = res.headers('x-csrf-jwt');
            _config.$meta.csrfJwt = res.headers('x-csrf-jwt');
            _config.$meta.headers['x-csrf-jwt-hash'] = res.headers('x-csrf-jwt-hash');
        }

        if (res.headers('X-cookies')) {

            _config.$meta.headers['x-cookies-hash'] = res.headers('x-cookies-hash');

            // Extend local cookie jar of encrypted-cookie-name->encrypted-cookie-value with any new cookies
            _util.$util.extend(_config.$meta.headers['x-cookies'], JSON.parse(res.headers('X-cookies') || '{}'));
        }

        latestCorrelationID = res.headers('Paypal-Debug-Id') || latestCorrelationID;
    },

    // Dispatch the response to the correct handlers
    handleResponse: function handleResponse(res, options) {
        var loggerName = 'ui_' + options.name;

        var loggerPayload = {
            name: options.name,
            method: options.method,
            uri: options.uri,
            time: options.duration,
            duration: options.duration
        };

        // Here we dispatch the response to the correct handler based on the ack.
        //
        // ack=success     -> options.success()
        // ack=error       -> options.error()
        // ack=validation  -> options.validation()
        // ack=contingency -> options.contingencies[contingency]()
        //
        // Why do this rather than just rejecting the promise with a custom error?
        //
        // a) Angular promises have no `.catchType()`, so this saves us a lot of `instanceof` checks
        // b) Angular promises have no `.done()`, so callers can not assert that they have handled
        //    every error they want to. This is exacerbated by the fact that there is no single thread
        //    of execution on the front-end a la node, so we can't just call `.done()` at the very end :(
        //
        // This way, if somebody passes a handler, it will be called. Otherwise, an error will be thrown
        // and the global error handler will be invoked. The api responses are also given as promises.

        var resultModel = options.resultModel || options.model;

        /* jshint maxcomplexity: 17 */
        return _promise.$promise.call(function () {

            // If we are given a `resultModel`, we always extend it with `res.data`, no matter the ack

            if (res.data && resultModel) {

                if (resultModel.populate) {
                    resultModel.populate(res.data);
                } else {
                    _util.$util.extend(resultModel, res.data);
                }
            }

            if (res && res.ack === 'success' && resultModel && resultModel.fetchChildren) {
                return _promise.$promise.resolve(resultModel.fetchChildren()).then(function (children) {
                    return _util.$util.extend(resultModel, children);
                });
            }
        }).then(function () {

            // Handle each ack type, throw errors accordingly

            if (res.ack === 'success') {
                _logger.$logger.info(loggerName + '_success', loggerPayload);

                if (options.success) {
                    if (options.success instanceof Function) {
                        return options.success(res.data);
                    }
                    return options.success;
                }

                return res;
            }

            if (options.failSilently) {
                return;
            }

            if (res.ack === 'contingency') {
                _logger.$logger.info(loggerName + '_contingency', _util.$util.extend(loggerPayload, {
                    contingency: res.contingency
                }));

                if (!res.contingency) {
                    throw new _error.$ApiError('expected_contingency_name', {
                        api: options.name
                    });
                }

                var contingency = new _error.$Contingency(res.contingency, { // eslint-disable-line no-reserved-keys
                    transient: options.transientError
                });

                if (resultModel) {
                    resultModel.errorData = res.errorData || {};
                }

                var handler = options.contingencies && (options.contingencies[contingency.message] || options.contingencies['DEFAULT']);

                _util.$util.extend(contingency, res.errorData);

                if (handler) {
                    if (handler instanceof Function) {
                        return handler(contingency.message, contingency);
                    }
                    return handler;
                }

                throw contingency;
            } else if (res.ack === 'validation') {
                _logger.$logger.info(loggerName + '_validation', _util.$util.extend(loggerPayload, {
                    validation: res.validation
                }));

                if (options.validation) {
                    return options.validation(res.validation);
                }

                throw new _error.$ApiError('validation', { // eslint-disable-line no-reserved-keys
                    transient: options.transientError
                });
            } else if (res.ack === 'permission_denied') {
                _logger.$logger.info(loggerName + '_denied', loggerPayload);

                throw new _error.$Forbidden(options.uri + ': forbidden', { // eslint-disable-line no-reserved-keys
                    transient: options.transientError
                });
            } else {
                _logger.$logger.info(loggerName + '_noack', loggerPayload);

                throw new _error.$ApiError('noack', { // eslint-disable-line no-reserved-keys
                    transient: options.transientError
                });
            }
        });
    },

    // Handle http error responses (ack=error or unhandled errors)
    handleErrorResponse: function handleErrorResponse(err, options) {

        return _promise.$promise.run(function () {

            // If the caller provides an error handler, let them handle it
            if (options.error) {
                return options.error(err);
            }

            // Otherwise we throw the error. Please not that this is NOT just rejecting the promise,
            // this will always end up in the global error handler due to how angular promises work.
            throw err;
        });
    },

    // Build the uri for the api call
    getURI: function getURI(model, action, relative) {
        var self = this;

        // build the uri as a seriest of segments
        var uri = [];

        uri.push(this.uri.replace(/\/:[\w\.]+\?/g, function (key) {

            // Add any *optional* dynamic keys from the model (/foo/:bar? -> /foo/{{model.bar}})

            key = key.slice(2);
            key = key.substring(0, key.length - 1);

            var component = model.get ? model.get(key) : model[key];

            if (!component) {
                return '';
            }

            return '/' + component;
        }).replace(/:[\w\.]+/g, function (key) {

            // Add any dynamic keys from the model (/foo/:bar -> /foo/{{model.bar}})

            key = key.slice(1);

            var component = model.get ? model.get(key) : model[key];

            if (!component) {
                throw new Error('Property "' + key + '" not found in model for: ' + self.uri);
            }

            return component;
        }));

        // Add the action, if provided (`foo.action('bar', {...})` -> /api/foo/bar
        if (action) {
            uri.push(action);
        }

        // Add the extension (e.g. for doing a GET on a .json resource)
        if (this.ext) {
            uri[uri.length - 1] += '.' + this.ext;
        }

        // Join the uri segments and strip any extra slashes
        uri = '/' + uri.join('/').replace(/\/{2,}/g, '/').replace(/^\//, '');

        // Add on the base uri, e.g. /webapps/hermes
        if (!relative) {
            uri = (this.baseURI || _config.$config.urls.baseUrl) + uri;
        }

        return uri;
    },

    // Attach meta data provided by the parent app
    buildMeta: function buildMeta() {
        if (metaBuilder) {
            return metaBuilder();
        }
    },

    // Build the name of the api for logging. /api/foo/bar -> api_foo_bar
    buildAPIName: function buildAPIName(options) {
        var self = this;
        var apiName = self.uri.replace(/^\/+/, ''). // Remove leading '/'
        replace(/\//g, '_'). // Replace remaining '/' with '_'
        replace(/\?(.*)/, ''). // Remove everything after '?'
        replace(/[^a-zA-Z0-9_]/g, '');

        apiName = options.action ? apiName + '_' + options.action : apiName;
        apiName = apiName.charAt(0) === '_' ? apiName.slice(1) : apiName;
        return apiName;
    }
});

$Api.reopenClass({
    clearCache: function clearCache() {
        cache = {};
    }
});

$Api.registerMetaBuilder = function (builder) {
    metaBuilder = builder;
};

var $batchApi = new $Api({
    uri: '/api/batch',
    postAttempts: 3
});

function $getLatestCorrelationID() {
    return latestCorrelationID;
}

angular.value("$Api", $Api);
angular.value("$getLatestCorrelationID", $getLatestCorrelationID);
_logger.$logger;

_event.$event;

_class.$Class;

_util.$util;

_promise.$promise;

_config.$config;

_config.$meta;

_error.$Contingency;

_error.$Forbidden;

_error.$ApiError;

_error.$BatchShortCircuit;

_loader.$loader;


//////////////////
// WEBPACK FOOTER
// ./components/squid/dist/core/api.js
// module id = ./components/squid/dist/core/api.js
// module chunks = 1